import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { SvgLogo } from '@actual-app/components/icons/logo';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import type { File } from '@actual-app/core/types/file';
import { css, keyframes } from '@emotion/css';

import { Icono } from '#components/mobile/ui/Icono';
import { TACTIL } from '#components/mobile/ui/tokens';

import { Astillero } from './Astillero';
import type { DatosBarco } from './Astillero';
import { claveArchivo, compartidoCon, nombreDueno } from './datos';
import { EscenaPuerto } from './EscenaPuerto';
import { elegirEscena } from './escenas';
import { Galeon } from './Galeon';
import { HojaPresupuesto } from './HojaPresupuesto';
import { momentoDelDia, PALETAS } from './momento';
import type { Momento } from './momento';
import { coloresBarco, leerPortada, PORTADAS } from './portada';
import type { Portada } from './portada';

/** Lo que tarda el velero en salir del puerto antes de abrir el presupuesto. */
export const DURACION_ZARPAR = 900;

type PuertoProps = {
  files: File[];
  currentUserId: string;
  esAdmin: boolean;
  onSelect: (file: File) => Promise<void>;
  onBorrar: (file: File) => void;
  onCompartir: (file: File) => void;
  onRenombrar: (file: File, nombre: string) => Promise<void>;
  /** Devuelve el texto del error, o null si salió bien. */
  onPasarAMiCuenta: (file: File) => Promise<string | null>;
  /** Crea y abre el presupuesto del astillero. */
  onBotar: (datos: DatosBarco) => Promise<void>;
  onImportar: () => void;
};

/**
 * Lista de presupuestos de Rumbo: un puerto de noche donde cada presupuesto
 * es un galeón amarrado (la maqueta A que eligió Vadik). Al tocarlo zarpa y se abre; con «…» se renombra, se le
 * cambia la portada, se comparte o se borra.
 */
export function Puerto({
  files,
  currentUserId,
  esAdmin,
  onSelect,
  onBorrar,
  onCompartir,
  onRenombrar,
  onPasarAMiCuenta,
  onBotar,
  onImportar,
}: PuertoProps) {
  const { t } = useTranslation();
  const [zarpando, setZarpando] = useState<string | null>(null);
  const [astillero, setAstillero] = useState(false);
  const onCrear = () => setAstillero(true);
  const [menu, setMenu] = useState<File | null>(null);
  // Se lee una vez por presupuesto; al cambiarla en la hoja se actualiza aquí.
  const [portadas, setPortadas] = useState<Record<string, Portada | null>>(() =>
    Object.fromEntries(
      files.map(f => [claveArchivo(f), leerPortada(claveArchivo(f))]),
    ),
  );
  const ocupado = useRef(false);
  // Cada vez que se entra al puerto: una escena al azar y el cielo de la hora.
  const [escena] = useState(() => elegirEscena());
  const [momento] = useState(() => momentoDelDia(new Date()));
  const paleta = PALETAS[momento];

  const zarpar = (file: File) => {
    if (ocupado.current) {
      return;
    }
    ocupado.current = true;
    const quieto = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    setZarpando(claveArchivo(file));
    window.setTimeout(
      () => {
        void onSelect(file).finally(() => {
          ocupado.current = false;
          setZarpando(null);
        });
      },
      quieto ? 0 : DURACION_ZARPAR,
    );
  };

  return (
    <View
      data-testid="puerto"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1,
        background: paleta.agua[1],
        color: '#e8eef7',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {/* Cielo de noche con luna, estrellas y faro */}
      <View
        style={{
          position: 'relative',
          flexShrink: 0,
          height: 'calc(max(230px, 40vh) + env(safe-area-inset-top))',
          background: `linear-gradient(180deg, ${paleta.cielo[0]} 0%, ${paleta.cielo[1]} 100%)`,
        }}
      >
        <Cielo momento={momento} />
        <View
          style={{
            position: 'relative',
            padding: 'calc(18px + env(safe-area-inset-top)) 16px 0',
            gap: 6,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <SvgLogo width={32} height={32} aria-hidden="true" />
            <h1
              style={{
                margin: 0,
                flex: 1,
                fontFamily: "'Fraunces', Georgia, 'Times New Roman', serif",
                fontSize: 23,
                fontWeight: 700,
                color: paleta.titulo,
              }}
            >
              <Trans>Your budgets</Trans>
            </h1>
          </View>
          <Text style={{ color: paleta.subtitulo, fontSize: 13 }}>
            {files.length > 0
              ? t('Tap a boat to set sail')
              : t('There are no boats in the harbour yet')}
          </Text>
        </View>
      </View>

      {/* Muelle con sus pilotes */}
      <View aria-hidden style={{ position: 'relative', flexShrink: 0 }}>
        <View style={{ height: 12, backgroundColor: '#6b4f3a' }} />
        {[7, 40, 73].map(x => (
          <View
            key={x}
            style={{
              position: 'absolute',
              top: 12,
              left: `${x}%`,
              width: 7,
              height: 40,
              backgroundColor: '#4e3929',
              zIndex: 1,
            }}
          />
        ))}
      </View>

      {/* Agua con los barcos amarrados */}
      <View
        style={{
          position: 'relative',
          flex: 1,
          flexShrink: 0,
          minHeight: 380,
          background: `linear-gradient(180deg, ${paleta.agua[0]} 0%, ${paleta.agua[1]} 100%)`,
        }}
      >
        <Olas />
        <View
          role="list"
          aria-label={t('Your budgets')}
          style={{
            position: 'relative',
            // Por encima de la escena divertida.
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            columnGap: 8,
            padding: '18px 10px 110px',
          }}
        >
          {files.map((file, i) => {
            const clave = claveArchivo(file);
            const colores = coloresBarco(clave, portadas[clave] ?? null);
            const con = compartidoCon(file, currentUserId);
            const de = nombreDueno(file, currentUserId);
            const ancho = TAMANOS[i % TAMANOS.length];
            return (
              <View
                role="listitem"
                key={clave}
                style={{
                  position: 'relative',
                  // El que zarpa pasa por encima de los demás barcos.
                  zIndex: zarpando === clave ? 3 : undefined,
                  // Desordenados como en un puerto de verdad.
                  marginTop: i % 2 === 1 ? 52 : i > 1 ? -18 : 0,
                  alignItems: i % 4 === 2 ? 'flex-start' : 'center',
                }}
              >
                <Button
                  variant="bare"
                  data-testid={`barco-${clave}`}
                  aria-label={t('Open {{name}}', { name: file.name })}
                  onPress={() => zarpar(file)}
                  isDisabled={zarpando !== null}
                  style={{
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    padding: '4px 2px',
                    borderRadius: 16,
                    color: 'inherit',
                    // El botón recorta por defecto: sin esto se cortaba la
                    // bandera al mecerse y el barco zarpaba dentro de su cuadro.
                    overflow: 'visible',
                  }}
                >
                  <View
                    className={css({
                      animation:
                        zarpando === clave
                          ? `${zarpa} ${DURACION_ZARPAR}ms cubic-bezier(.4,0,.6,1) forwards`
                          : `${mece} ${2.4 + (i % 3) * 0.3}s ease-in-out ${-i * 0.8}s infinite`,
                      transformOrigin: '50% 90%',
                      '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                      },
                    })}
                  >
                    <Galeon
                      {...colores}
                      etiqueta={de ?? (con.length > 0 ? con.join(', ') : null)}
                      width={ancho}
                    />
                  </View>
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: 700,
                      textAlign: 'center',
                      overflowWrap: 'anywhere',
                      color: '#e8eef7',
                      maxWidth: 150,
                    }}
                  >
                    {file.name}
                  </Text>
                  <Text
                    style={{
                      fontSize: 11,
                      color: con.length > 0 || de ? '#8fd3b0' : '#9aa6bd',
                      textAlign: 'center',
                    }}
                  >
                    {de
                      ? `⚑ ${t('From {{name}}', { name: de })}`
                      : con.length > 0
                        ? `⚑ ${t('With {{names}}', { names: con.join(', ') })}`
                        : null}
                  </Text>
                  {!de && con.length === 0 && <Amarrado file={file} />}
                </Button>
                <Button
                  variant="bare"
                  data-testid={`menu-barco-${clave}`}
                  aria-label={t('Options for {{name}}', { name: file.name })}
                  onPress={() => setMenu(file)}
                  style={{
                    position: 'absolute',
                    top: -6,
                    right: 0,
                    width: TACTIL,
                    height: TACTIL,
                    borderRadius: 999,
                    color: '#cfd8e6',
                  }}
                >
                  <Icono nombre="more" size={20} />
                </Button>
              </View>
            );
          })}
          <View
            style={{
              marginTop: files.length % 2 === 1 ? 52 : 18,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              variant="bare"
              data-testid="botar-barco"
              onPress={onCrear}
              style={{
                width: 120,
                height: 58,
                borderRadius: 14,
                border: '1.5px dashed rgba(143, 211, 176, .55)',
                color: '#8fd3b0',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <Trans>+ Launch a boat</Trans>
            </Button>
          </View>
        </View>
      </View>

      <EscenaPuerto escena={escena} />

      <View
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          alignItems: 'center',
          padding: '12px 0 calc(14px + env(safe-area-inset-bottom))',
          background: `linear-gradient(0deg, ${paleta.agua[1]} 45%, transparent)`,
        }}
      >
        <Button
          variant="bare"
          onPress={onImportar}
          style={{ minHeight: TACTIL, color: '#8fd3b0', fontWeight: 600 }}
        >
          <Trans>Import a backup</Trans>
        </Button>
      </View>

      {astillero && (
        <Astillero
          colorInicial={PORTADAS[files.length % PORTADAS.length].id}
          onBotar={onBotar}
          onCerrar={() => setAstillero(false)}
        />
      )}

      {menu && (
        <HojaPresupuesto
          file={menu}
          currentUserId={currentUserId}
          esAdmin={esAdmin}
          portada={portadas[claveArchivo(menu)] ?? null}
          onCerrar={() => setMenu(null)}
          onAbrir={() => {
            setMenu(null);
            zarpar(menu);
          }}
          onRenombrar={nombre => onRenombrar(menu, nombre)}
          onPortada={portada =>
            setPortadas(actual => ({
              ...actual,
              [claveArchivo(menu)]: portada,
            }))
          }
          onCompartir={() => {
            setMenu(null);
            onCompartir(menu);
          }}
          onPasarAMiCuenta={async () => {
            // El error se enseña dentro de la hoja: los avisos normales
            // quedan tapados por el puerto y no se veían.
            const error = await onPasarAMiCuenta(menu);
            if (!error) {
              setMenu(null);
            }
            return error;
          }}
          onBorrar={() => {
            setMenu(null);
            onBorrar(menu);
          }}
        />
      )}
    </View>
  );
}

/** Ancho de cada galeón, para que no parezcan todos iguales. */
const TAMANOS = [132, 104, 96, 118];

/** Sol o luna, estrellas, nubes, gaviotas y el faro, según la hora. */
function Cielo({ momento }: { momento: Momento }) {
  const paleta = PALETAS[momento];
  return (
    <>
      <svg
        aria-hidden
        viewBox="0 0 360 230"
        preserveAspectRatio="xMidYMax slice"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <g fill="#dff3e8" opacity={paleta.estrellas}>
          <circle cx="40" cy="100" r="1" />
          <circle cx="96" cy="128" r="1.2" />
          <circle cx="170" cy="92" r="1" />
          <circle cx="214" cy="150" r="1" />
          <circle cx="70" cy="170" r="1.2" />
          <circle cx="130" cy="60" r="1" />
          <circle cx="200" cy="40" r="1.1" />
          <circle cx="330" cy="60" r="0.9" />
          <circle cx="150" cy="190" r="0.9" />
          <circle cx="300" cy="170" r="1" />
          <circle cx="250" cy="110" r="0.9" />
        </g>
        {paleta.astro === 'luna' ? (
          <>
            <circle cx="292" cy="112" r="18" fill="#dff3e8" opacity="0.9" />
            <circle cx="299" cy="106" r="16" fill="#14223a" />
          </>
        ) : (
          <>
            <circle
              cx="290"
              cy="112"
              r="44"
              fill={paleta.colorSol}
              opacity="0.18"
            />
            <circle cx="290" cy="112" r="20" fill={paleta.colorSol} />
          </>
        )}
        {paleta.nubes && (
          <g fill="#ffffff">
            <g
              className={css({
                animation: `${nube} 40s linear infinite`,
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              })}
            >
              <g transform="translate(0,96)" opacity="0.85">
                <ellipse cx="30" cy="10" rx="26" ry="10" />
                <ellipse cx="50" cy="4" rx="18" ry="11" />
                <ellipse cx="14" cy="6" rx="12" ry="8" />
              </g>
            </g>
            <g
              className={css({
                animation: `${nube} 55s linear -20s infinite`,
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              })}
            >
              <g transform="translate(0,170)" opacity="0.7">
                <ellipse cx="30" cy="10" rx="30" ry="9" />
                <ellipse cx="54" cy="5" rx="16" ry="9" />
              </g>
            </g>
            <g
              stroke="#1d3550"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              className={css({
                animation: `${gaviota} 14s linear infinite`,
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              })}
            >
              <path d="M0 140 q5 -5 10 0 q5 -5 10 0" />
              <path d="M26 154 q4 -4 8 0 q4 -4 8 0" />
            </g>
          </g>
        )}
      </svg>
      {/* El faro va aparte para que no se recorte en pantallas anchas o altas. */}
      <svg
        aria-hidden
        viewBox="0 0 26 100"
        width={26}
        height={100}
        style={{ position: 'absolute', left: 18, bottom: 0 }}
      >
        <g>
          <rect x="6" y="20" width="13" height="80" fill="#dff3e8" />
          <rect x="6" y="42" width="13" height="9" fill="#e0766b" />
          <rect x="6" y="66" width="13" height="9" fill="#e0766b" />
          <path d="M3 20 L12.5 7 L22 20 Z" fill="#2e8b62" />
          <circle
            cx="12.5"
            cy="14.5"
            r="3.2"
            fill="#ffe9a8"
            className={css({
              animation: `${destella} 3.2s ease-in-out infinite`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            })}
          />
        </g>
      </svg>
    </>
  );
}

/** Dónde está amarrado el barco: en la nube, o aún pendiente de subir. */
function Amarrado({ file }: { file: File }) {
  const { t } = useTranslation();
  const amarre = amarreDe(file.state, t);
  return (
    <Text
      data-testid="amarre-barco"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        color: amarre.local ? '#f3d9a4' : amarre.nube ? '#8fd3b0' : '#9aa6bd',
      }}
    >
      {amarre.icono && <Icono nombre={amarre.icono} size={12} />}
      {amarre.texto}
    </Text>
  );
}

type AmarreBarco = {
  texto: string;
  icono: 'nube' | null;
  nube: boolean;
  local: boolean;
};

/** Etiqueta de cada estado del presupuesto. */
export function amarreDe(
  estado: File['state'],
  t: (clave: string) => string,
): AmarreBarco {
  switch (estado) {
    case 'synced':
      return {
        texto: t('Phone and cloud'),
        icono: 'nube',
        nube: true,
        local: false,
      };
    case 'remote':
      return {
        texto: t('In the cloud, tap to download'),
        icono: 'nube',
        nube: true,
        local: false,
      };
    // Botado sin conexión: `SubidaAutomatica` lo sube al abrirlo con conexión.
    case 'local':
      return {
        texto: t('Waiting to upload'),
        icono: 'nube',
        nube: false,
        local: true,
      };
    case 'detached':
      return {
        texto: t('Not syncing'),
        icono: null,
        nube: false,
        local: false,
      };
    case 'broken':
      return {
        texto: t('Cannot be opened'),
        icono: null,
        nube: false,
        local: false,
      };
    default:
      return {
        texto: t('No connection'),
        icono: null,
        nube: false,
        local: false,
      };
  }
}

/** Líneas de olas cada 90 px que se desplazan despacio. */
function Olas() {
  const d = Array.from(
    { length: 70 },
    (_, i) => `Q${i * 30 + 15} 2 ${i * 30 + 30} 8`,
  ).join(' ');
  return (
    <View
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {Array.from({ length: 12 }, (_, i) => 40 + i * 90).map((y, i) => (
        <svg
          key={y}
          viewBox="0 0 2100 12"
          width={2100}
          height={12}
          className={css({
            position: 'absolute',
            top: y,
            left: -60,
            animation: `${deriva} ${6 + (i % 3) * 2}s linear infinite`,
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          })}
        >
          <path
            d={`M0 8 ${d}`}
            stroke="#8fd3b0"
            strokeOpacity={0.18}
            fill="none"
            strokeWidth={1.2}
          />
        </svg>
      ))}
    </View>
  );
}

const mece = keyframes({
  '0%, 100%': { transform: 'translateY(0) rotate(-2.5deg)' },
  '50%': { transform: 'translateY(4px) rotate(2.5deg)' },
});

const zarpa = keyframes({
  '0%': { transform: 'translate(0, 0) rotate(0deg) scale(1)', opacity: 1 },
  '20%': { transform: 'translate(-8px, 2px) rotate(-4deg) scale(1)' },
  '90%': { opacity: 1 },
  // Cruza toda la pantalla hacia la derecha hasta perderse.
  '100%': {
    transform: 'translate(110vw, -40px) rotate(6deg) scale(.6)',
    opacity: 0,
  },
});

const nube = keyframes({
  from: { transform: 'translateX(-140px)' },
  to: { transform: 'translateX(420px)' },
});

const gaviota = keyframes({
  from: { transform: 'translate(-40px, 0)' },
  '50%': { transform: 'translate(180px, -20px)' },
  to: { transform: 'translate(420px, 10px)' },
});

const destella = keyframes({
  '0%, 100%': { opacity: 1 },
  '50%': { opacity: 0.35 },
});

const deriva = keyframes({
  from: { transform: 'translateX(0)' },
  to: { transform: 'translateX(-60px)' },
});
